import React, { useState } from "react";
import {
  User,
  Shield,
  Mail,
  Key,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Clock,
} from "lucide-react";
import api from "../../../services/api";
import Navbar from "../../../components/Navbar";
import "./Profile.css";

const Profile = ({ role }) => {
  const [user] = useState(JSON.parse(localStorage.getItem("user")));
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage("New password and confirm password do not match");
      return;
    }

    try {
      const response = await api.post("/auth/change-password", {
        email: user.email,
        currentPassword,
        newPassword,
      });

      setMessage(response.data.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      // Refresh the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Change password failed:", error.response || error);
      setMessage(error.response?.data?.message || "Error changing password");
    }
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return "Not available";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  return (
    <div className="profile-page">
      <Navbar role={role} />

      <div className="profile-content">
        <div className="profile-header">
          <div className="header-info">
            <h1>My Profile</h1>
          </div>
          <div className="user-welcome">
            <h2>{user.name}</h2>
            <div className="role-badge">
              <Shield size={16} />
              <span>{role}</span>
            </div>
          </div>
        </div>

        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === "info" ? "active" : ""}`}
            onClick={() => setActiveTab("info")}
          >
            <User size={18} />
            Profile Information
          </button>
          <button
            className={`profile-tab ${
              activeTab === "security" ? "active" : ""
            }`}
            onClick={() => setActiveTab("security")}
          >
            <Key size={18} />
            Security Settings
          </button>
        </div>

        <div className="profile-body">
          {activeTab === "info" && (
            <div className="profile-card">
              <div className="profile-card-header">
                <User size={22} />
                <h2>Personal Information</h2>
              </div>

              <div className="profile-info-grid">
                <div className="info-item">
                  <div className="info-label">
                    <User size={16} />
                    <span>Full Name</span>
                  </div>
                  <div className="info-value">{user.name}</div>
                </div>

                <div className="info-item">
                  <div className="info-label">
                    <Mail size={16} />
                    <span>Email Address</span>
                  </div>
                  <div className="info-value">{user.email}</div>
                </div>

                <div className="info-item">
                  <div className="info-label">
                    <Shield size={16} />
                    <span>Role</span>
                  </div>
                  <div className="info-value">{user.role}</div>
                </div>

                <div className="info-item">
                  <div className="info-label">
                    <CheckCircle size={16} />
                    <span>Account Status</span>
                  </div>
                  <div className="info-value">
                    <span className="status-active">Active</span>
                  </div>
                </div>

                <div className="info-item span-full">
                  <div className="info-label">
                    <Clock size={16} />
                    <span>Member Since</span>
                  </div>
                  <div className="info-value">
                    {getFormattedDate(user.created_at)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="profile-card">
              <div className="profile-card-header">
                <Key size={22} />
                <h2>Change Password</h2>
              </div>

              <form onSubmit={handleChangePassword} className="password-form">
                <div className="form-group">
                  <label htmlFor="currentPassword" className="required">
                    Current Password
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      aria-label="Toggle password visibility"
                    >
                      {showCurrentPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword" className="required">
                    New Password
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" className="required">
                    Confirm New Password
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      aria-label="Toggle password visibility"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                {message && (
                  <div
                    className={`message-alert ${
                      message.includes("Error") ? "error" : "success"
                    }`}
                  >
                    {message.includes("Error") ? (
                      <AlertCircle size={20} />
                    ) : (
                      <CheckCircle size={20} />
                    )}
                    <span>{message}</span>
                  </div>
                )}

                <button type="submit" className="submit-button">
                  Update Password
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
